export interface RealAprResult {
  /** True APR computed from uncurtailed, non-outlier vehicles. Null when no data. */
  apr: number | null;
  /** Interest-only APR (no fees). Null when no data. */
  interestOnlyApr: number | null;
  avgInterestPerUnit: number | null;
  avgFeesPerUnit: number | null;
  totalUnits: number;
  stmtCount: number;

  /** How many vehicles were included in the True APR calculation. */
  usedCount: number;
  /** How many vehicles were excluded (curtailed or APR outliers). */
  excludedCount: number;
  /**
   * True when the True APR could not be computed because fewer than 3 uncurtailed
   * vehicles with positive interest remained after filtering.
   */
  insufficientData: boolean;
  /**
   * True when all qualifying uncurtailed vehicles have $0 interest — rate cannot
   * be back-calculated.
   */
  noInterestAccrued: boolean;
  /**
   * True when all qualifying uncurtailed vehicles have $0 interest but the lender
   * charges 0% interest (verified zero-interest lender). The APR is computed from
   * fees alone and interestOnlyApr is 0.
   */
  isZeroInterest: boolean;
  /**
   * True when the result fell back to statement-level totals because no per-vehicle
   * originalAmount data was available. When true, the rate is an ESTIMATE only and
   * must ALWAYS be labelled as such — never displayed as a verified True APR.
   */
  isEstimate: boolean;
  /**
   * True when the uncurtailed path had insufficient data (<3 qualifying vehicles)
   * but the result was computed from ALL per-unit rows as a fallback estimate.
   * Always paired with isEstimate=true. Never silently treated as a verified rate.
   */
  isFallbackAllUnits: boolean;
  /**
   * True when the blended APR includes the dealer's manually entered per-unit fee
   * (lender card feePeriod1) for units whose statement shows no fee charged yet.
   * Interest-only APR is never affected by entered fees.
   */
  includesEnteredFee: boolean;
}

/**
 * Extract the dealer-entered per-unit fee from a lender record (feePeriod1).
 * Returns null when absent/zero — callers pass this as manualFeePerUnit so
 * uncharged (typically uncurtailed) units still carry their known fee load
 * in the blended borrow rate.
 */
export function manualFeePerUnitFromLender(lender: any): number | null {
  const v = lender?.feePeriod1 != null ? parseFloat(String(lender.feePeriod1)) : 0;
  return Number.isFinite(v) && v > 0 ? v : null;
}

/** Compute the median of an array. Returns null for empty arrays. */
function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/**
 * Compute an average interest rate from ALL valid per-unit rows (ignoring curtailment).
 * Used as a fallback when the uncurtailed path has insufficient data.
 * Returns null if no qualifying rows exist.
 */
function computeAllUnitsFallbackRate(rows: any[]): number | null {
  const qualifying = rows.filter(
    (r) => (r.principal ?? 0) > 0 && (r.daysOutstanding ?? 0) > 0 && (r.interest ?? 0) > 0
  );
  if (qualifying.length === 0) return null;
  const rates = qualifying.map(
    (r: any) => ((r.interest ?? 0) / (r.principal ?? 1) / (r.daysOutstanding ?? 1)) * 365 * 100
  );
  return rates.reduce((s, v) => s + v, 0) / rates.length;
}

/**
 * Compute an average (interest + fees) blended rate from ALL valid per-unit rows.
 * Used for the "Cost to Borrow All In" field so fees are included alongside interest.
 * Returns null if no qualifying rows exist.
 */
function computeAllUnitsBlendedRate(rows: any[], manualFeePerUnit = 0): number | null {
  const effFees = (r: any) => ((r.fees ?? 0) > 0 ? (r.fees ?? 0) : manualFeePerUnit);
  const qualifying = rows.filter(
    (r) => (r.principal ?? 0) > 0 && (r.daysOutstanding ?? 0) > 0
      && ((r.interest ?? 0) + (r.fees ?? 0)) > 0
  );
  if (qualifying.length === 0) return null;
  const rates = qualifying.map(
    (r: any) => (((r.interest ?? 0) + effFees(r)) / (r.principal ?? 1) / (r.daysOutstanding ?? 1)) * 365 * 100
  );
  return rates.reduce((s, v) => s + v, 0) / rates.length;
}

/**
 * Computes the True APR for a lender from its statement(s).
 *
 * Methodology (curtailment-filtered):
 *   1. Filter to uncurtailed vehicles: |principal - originalAmount| <= $1
 *   2. Compute per-vehicle interest-only APR = (interest / principal / days) * 365 * 100
 *   3. Compute the median APR across the clean set
 *   4. Exclude vehicles whose APR deviates more than 2 percentage points from the median
 *      (likely grace-period or promo vehicles)
 *   5. Return the average APR of the remaining clean set
 *
 * When no per-vehicle originalAmount data is available, falls back to statement-level
 * totals — but always sets isEstimate=true so the UI can label it appropriately.
 * The fallback result must NEVER be silently treated as a verified True APR.
 *
 * If fewer than 3 uncurtailed vehicles remain, sets insufficientData=true.
 * If all qualifying vehicles show $0 interest, sets noInterestAccrued=true.
 */

/**
 * Deduplicate receivables across multiple statements by VIN/stock key.
 * Statements must be ordered newest-first (the API returns them that way via `desc(createdAt)`).
 * For each unique VIN or stock number, keeps the receivable from the most recent statement.
 * Receivables with no VIN and no stock key are always included (cannot be matched/keyed).
 *
 * This prevents duplicate rows created by retry-runs of the same job from inflating counts,
 * and correctly handles a vehicle appearing on multiple monthly statements (last month wins).
 */
export function dedupeReceivables(statements: any[]): any[] {
  const seen = new Set<string>();
  const result: any[] = [];
  for (const stmt of statements) {
    let recvs: any[] = [];
    try { recvs = JSON.parse(stmt.receivablesJson ?? "[]"); } catch {}
    for (const r of recvs) {
      const key = String(r.vin ?? r.stock ?? "").trim().toUpperCase();
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      result.push(r);
    }
  }
  return result;
}

export function computeRealAprForLender(
  statements: any[],
  opts?: { manualFeePerUnit?: number | null },
): RealAprResult {
  const manualFee = opts?.manualFeePerUnit != null && opts.manualFeePerUnit > 0 ? opts.manualFeePerUnit : 0;
  // For blended (all-in) rates only: rows the statement hasn't charged a fee on
  // yet carry the dealer-entered per-unit fee so known fee load isn't invisible.
  const effFees = (r: any) => ((r.fees ?? 0) > 0 ? (r.fees ?? 0) : manualFee);
  const usesManualFee = (rows: any[]) => manualFee > 0 && rows.some((r: any) => (r.fees ?? 0) <= 0);
  // Exact qualifying subset used by computeAllUnitsBlendedRate — the flag must be
  // derived from the rows that actually contribute to the returned blended rate.
  // Zero-charge rows (no interest AND no statement fee) intentionally stay OUT of
  // the fallback (unchanged invariant): with no interest info their rate can't be
  // measured, so the manual fee is never applied to them.
  const blendQualifying = (rows: any[]) => rows.filter(
    (r: any) => (r.principal ?? 0) > 0 && (r.daysOutstanding ?? 0) > 0
      && ((r.interest ?? 0) + (r.fees ?? 0)) > 0
  );

  const empty: RealAprResult = {
    apr: null, interestOnlyApr: null,
    avgInterestPerUnit: null, avgFeesPerUnit: null,
    totalUnits: 0, stmtCount: 0,
    usedCount: 0, excludedCount: 0,
    insufficientData: false, noInterestAccrued: false, isZeroInterest: false, isEstimate: false,
    isFallbackAllUnits: false, includesEnteredFee: false,
  };

  if (statements.length === 0) return empty;

  const allRecvs = dedupeReceivables(statements);

  const stmtsWithData = statements.filter(
    (s) => parseFloat(s.totalInterestPaid) > 0 || parseFloat(s.totalFeesPaid) > 0
      || (() => {
        try {
          const rs = JSON.parse(s.receivablesJson ?? "[]");
          // Count statements with any receivable that has a valid principal (even if zero interest)
          // so that "no interest accrued yet" detection can work correctly.
          return rs.some((r: any) => (r.principal ?? 0) > 0);
        } catch { return false; }
      })()
  );
  const stmtCount = stmtsWithData.length;
  if (stmtCount === 0) return empty;

  const allValidRecvs = allRecvs.filter((r) => (r.principal ?? 0) > 0);
  const totalUnits = allValidRecvs.length;

  // Per-unit rows with enough data for APR math.
  // A $0 interest+fees row normally means the extraction MISSED the value, so it
  // is excluded. Exception: rows the parser math-proved as genuinely zero-charge
  // (zeroChargesVerified — printed row Total === Principal, identity validated)
  // ARE included so legit $0 units count toward the per-unit averages.
  // chargedRecvs is the ORIGINAL qualification (charges > 0) and remains the sole
  // basis for all APR/rate math below — verified-zero rows must never dilute rates.
  const chargedRecvs = allValidRecvs.filter(
    (r) => (r.daysOutstanding ?? 0) > 0 && ((r.interest ?? 0) + (r.fees ?? 0)) > 0
  );
  const qualRecvs = allValidRecvs.filter(
    (r) => (r.daysOutstanding ?? 0) > 0
      && (((r.interest ?? 0) + (r.fees ?? 0)) > 0 || r.zeroChargesVerified === true)
  );

  // Avg stats (always computed regardless of curtailment filtering)
  const avgInterestPerUnit = qualRecvs.length > 0
    ? qualRecvs.reduce((s: number, r: any) => s + (r.interest ?? 0), 0) / qualRecvs.length
    : null;
  const avgFeesPerUnit = qualRecvs.length > 0
    ? qualRecvs.reduce((s: number, r: any) => s + (r.fees ?? 0) - (r.collateralFee ?? 0), 0) / qualRecvs.length
    : null;

  // Branch condition intentionally uses chargedRecvs (pre-existing behavior):
  // verified zero-charge rows alone must not divert from the statement-level fallback.
  if (chargedRecvs.length > 0 || allValidRecvs.some((r: any) => (r.originalAmount ?? 0) > 0)) {
    // ── True APR: curtailment-filtered ────────────────────────────────────
    // Use allValidRecvs (principal > 0) as the base for uncurtailed detection so that
    // zero-interest uncurtailed vehicles are captured for the noInterestAccrued check.
    const hasOriginalAmounts = allValidRecvs.some((r: any) => (r.originalAmount ?? 0) > 0);

    if (hasOriginalAmounts) {
      // Step 1: filter ALL valid vehicles (not just charged ones) to uncurtailed
      // so that zero-interest vehicles don't silently disappear before the edge-case check.
      const uncurtailedAll = allValidRecvs.filter((r: any) => {
        const orig = r.originalAmount ?? 0;
        const cur = r.principal ?? 0;
        if (orig <= 0) return false; // no originalAmount → skip
        return Math.abs(cur - orig) <= Math.max(1, orig * 0.005);
      });

      const excludedByNoData = allValidRecvs.filter((r: any) => (r.originalAmount ?? 0) <= 0).length;
      const curtailedCount = allValidRecvs.length - uncurtailedAll.length - excludedByNoData;

      // Step 2: detect "no interest accrued" — checked BEFORE filtering to interest > 0
      const uncurtailedWithInterest = uncurtailedAll.filter((r: any) => (r.interest ?? 0) > 0);
      if (uncurtailedAll.length >= 3 && uncurtailedWithInterest.length === 0) {
        // 0% interest lender: compute fees-only APR for display; interestOnlyApr = 0.
        // Denominator uses all valid uncurtailed rows with days > 0 (not filtered by fees).
        const uncurtailedWithDays = uncurtailedAll.filter(
          (r: any) => (r.daysOutstanding ?? 0) > 0
        );
        const sumFees = uncurtailedWithDays.reduce((s: number, r: any) => s + effFees(r), 0);
        const sumWeightedPrincipal = uncurtailedWithDays.reduce(
          (s: number, r: any) => s + (r.principal ?? 0) * (r.daysOutstanding ?? 0), 0
        );
        const feesOnlyApr = sumWeightedPrincipal > 0
          ? (sumFees / sumWeightedPrincipal) * 365 * 100
          : 0;
        return {
          apr: feesOnlyApr,
          includesEnteredFee: usesManualFee(uncurtailedWithDays),
          interestOnlyApr: 0,
          avgInterestPerUnit,
          avgFeesPerUnit,
          totalUnits,
          stmtCount,
          usedCount: uncurtailedAll.length,
          excludedCount: curtailedCount + excludedByNoData,
          insufficientData: false,
          noInterestAccrued: false,
          isZeroInterest: true,
          isEstimate: false,
          isFallbackAllUnits: false,
        };
      }

      // Step 3: per-vehicle interest-only APR (must have days > 0 and interest > 0)
      type VehicleWithApr = { r: any; aprPct: number };
      const withApr: VehicleWithApr[] = uncurtailedWithInterest
        .filter((r: any) => (r.daysOutstanding ?? 0) > 0)
        .map((r: any) => ({
          r,
          aprPct: ((r.interest ?? 0) / (r.principal ?? 1) / (r.daysOutstanding ?? 1)) * 365 * 100,
        }));

      // Insufficient data check — try all-units fallback before returning insufficientData
      if (withApr.length < 3) {
        const fallbackRate = computeAllUnitsFallbackRate(allValidRecvs);
        if (fallbackRate !== null) {
          const blendedRate = computeAllUnitsBlendedRate(allValidRecvs, manualFee) ?? fallbackRate;
          return {
            apr: blendedRate,
            includesEnteredFee: usesManualFee(blendQualifying(allValidRecvs)),
            interestOnlyApr: fallbackRate,
            avgInterestPerUnit,
            avgFeesPerUnit,
            totalUnits,
            stmtCount,
            usedCount: withApr.length,
            excludedCount: allValidRecvs.length - withApr.length,
            insufficientData: false,
            noInterestAccrued: false,
            isZeroInterest: false,
            isEstimate: true,
            isFallbackAllUnits: true,
          };
        }
        return {
      includesEnteredFee: false,
          apr: null,
          interestOnlyApr: null,
          avgInterestPerUnit,
          avgFeesPerUnit,
          totalUnits,
          stmtCount,
          usedCount: withApr.length,
          excludedCount: allValidRecvs.length - withApr.length,
          insufficientData: true,
          noInterestAccrued: false,
          isZeroInterest: false,
          isEstimate: false,
          isFallbackAllUnits: false,
        };
      }

      // Step 3: median APR of uncurtailed-with-interest
      const aprValues = withApr.map((v) => v.aprPct);
      const med = median(aprValues)!;

      // Step 4: remove outliers > 2 percentage points from median
      const clean = withApr.filter((v) => Math.abs(v.aprPct - med) <= 2);
      const outlierCount = withApr.length - clean.length;

      // Need at least 3 clean vehicles after outlier removal
      if (clean.length < 3) {
        // Outlier removal was too aggressive — try all-units fallback before returning insufficientData
        const fallbackRate = computeAllUnitsFallbackRate(allValidRecvs);
        if (fallbackRate !== null) {
          const blendedRate = computeAllUnitsBlendedRate(allValidRecvs, manualFee) ?? fallbackRate;
          return {
            apr: blendedRate,
            includesEnteredFee: usesManualFee(blendQualifying(allValidRecvs)),
            interestOnlyApr: fallbackRate,
            avgInterestPerUnit,
            avgFeesPerUnit,
            totalUnits,
            stmtCount,
            usedCount: clean.length,
            excludedCount: curtailedCount + excludedByNoData + outlierCount,
            insufficientData: false,
            noInterestAccrued: false,
            isZeroInterest: false,
            isEstimate: true,
            isFallbackAllUnits: true,
          };
        }
        return {
      includesEnteredFee: false,
          apr: null,
          interestOnlyApr: null,
          avgInterestPerUnit,
          avgFeesPerUnit,
          totalUnits,
          stmtCount,
          usedCount: clean.length,
          excludedCount: curtailedCount + excludedByNoData + outlierCount,
          insufficientData: true,
          noInterestAccrued: false,
          isZeroInterest: false,
          isEstimate: false,
          isFallbackAllUnits: false,
        };
      }

      // Step 5: average APR of the clean set (interest-only)
      const trueInterestApr = clean.reduce((s, v) => s + v.aprPct, 0) / clean.length;
      // Blended (interest+fees) APR for the clean set — rows with no statement
      // fee yet carry the dealer-entered per-unit fee (effFees).
      const sumCosts = clean.reduce((s, v) => s + (v.r.interest ?? 0) + effFees(v.r), 0);
      const sumW = clean.reduce((s, v) => s + (v.r.principal ?? 0) * (v.r.daysOutstanding ?? 0), 0);
      const blendedApr = sumW > 0 ? (sumCosts / sumW) * 365 * 100 : null;

      return {
        apr: blendedApr,
        includesEnteredFee: usesManualFee(clean.map((v) => v.r)),
        interestOnlyApr: trueInterestApr,
        avgInterestPerUnit,
        avgFeesPerUnit,
        totalUnits,
        stmtCount,
        usedCount: clean.length,
        excludedCount: curtailedCount + excludedByNoData + outlierCount,
        insufficientData: false,
        noInterestAccrued: false,
        isZeroInterest: false,
        isEstimate: false,
        isFallbackAllUnits: false,
      };
    }

    // ── No originalAmount data — blended rate across all charge-positive rows ──
    // (chargedRecvs, NOT qualRecvs: verified zero-charge rows are included in the
    // per-unit averages above but must not dilute the rate estimate here.)
    const sumAllCosts = chargedRecvs.reduce((s: number, r: any) => s + (r.interest ?? 0) + effFees(r), 0);
    const sumWeighted = chargedRecvs.reduce((s: number, r: any) => s + (r.principal ?? 0) * (r.daysOutstanding ?? 0), 0);
    const apr = sumWeighted > 0 ? (sumAllCosts / sumWeighted) * 365 * 100 : null;

    const perUnitRates = chargedRecvs
      .map((r: any) => {
        const w = (r.principal ?? 0) * (r.daysOutstanding ?? 0);
        return w > 0 ? ((r.interest ?? 0) / w) * 365 * 100 : null;
      })
      .filter((v): v is number => v !== null);
    const interestOnlyApr =
      perUnitRates.length > 0 ? perUnitRates.reduce((s, v) => s + v, 0) / perUnitRates.length : 0;

    return {
      apr,
      includesEnteredFee: usesManualFee(chargedRecvs),
      interestOnlyApr,
      avgInterestPerUnit,
      avgFeesPerUnit,
      totalUnits,
      stmtCount,
      usedCount: chargedRecvs.length,
      excludedCount: 0,
      insufficientData: false,
      noInterestAccrued: false,
      isZeroInterest: false,
      // No originalAmount → this is an estimate, not a verified True APR
      isEstimate: true,
      isFallbackAllUnits: false,
    };
  }

  // ── Fallback: no per-unit receivable rows — use statement-level totals ──
  const best = stmtsWithData[0];
  const totalInterest = parseFloat(best.totalInterestPaid || "0");
  const totalFees = parseFloat(best.totalFeesPaid || "0");
  const avgBal = parseFloat(best.averageBalance || "0");
  let fallbackUnits = best.unitsSold || 0;
  let fallbackRecvs: any[] = [];
  try { fallbackRecvs = JSON.parse(best.receivablesJson ?? "[]"); } catch {}
  if (fallbackUnits === 0) {
    fallbackUnits = fallbackRecvs.length;
  }
  const totalCollateralFees = fallbackRecvs.reduce((s: number, r: any) => s + (r.collateralFee ?? 0), 0);
  const annualDivisor =
    parseFloat(best.avgDaysInStock || "0") > 0
      ? parseFloat(best.avgDaysInStock)
      : best.periodDays || 30;
  const apr =
    avgBal > 0 && totalInterest + totalFees > 0
      ? ((totalInterest + totalFees) / avgBal) * (365 / annualDivisor) * 100
      : null;
  const interestOnlyApr =
    avgBal > 0 && totalInterest > 0
      ? (totalInterest / avgBal) * (365 / annualDivisor) * 100
      : 0;

  return {
      includesEnteredFee: false,
    apr,
    interestOnlyApr,
    avgInterestPerUnit: fallbackUnits > 0 ? totalInterest / fallbackUnits : null,
    avgFeesPerUnit: fallbackUnits > 0 ? (totalFees - totalCollateralFees) / fallbackUnits : null,
    totalUnits: fallbackUnits,
    stmtCount,
    usedCount: 0,
    excludedCount: 0,
    insufficientData: false,
    noInterestAccrued: false,
    isZeroInterest: false,
    // Statement-level totals → always an estimate
    isEstimate: true,
    isFallbackAllUnits: false,
  };
}

/**
 * Compute True APR directly from an array of editable receivable rows
 * (used in the statement review UI to pre-fill the interest rate field).
 */
export interface TrueAprAnnotation {
  apr: number | null;
  usedCount: number;
  excludedCount: number;
  totalCount: number;
  insufficientData: boolean;
  noInterestAccrued: boolean;
  isZeroInterest: boolean;
  isEstimate: boolean;
  label: string;
}

export function computeTrueAprFromRows(rows: {
  principal?: number | string;
  originalAmount?: number | string;
  daysOutstanding?: number | string;
  interest?: number | string;
  fees?: number | string;
}[]): TrueAprAnnotation {
  const n = (v: any) => (v != null ? parseFloat(String(v)) || 0 : 0);

  // Step 0: basic validity — must have positive principal
  const validRows = rows.filter((r) => n(r.principal) > 0);
  const totalCount = validRows.length;
  const hasOriginalAmounts = validRows.some((r) => n(r.originalAmount) > 0);

  if (!hasOriginalAmounts) {
    // No originalAmount data — cannot apply curtailment filter.
    // Fall back to per-unit blended estimate marked as isEstimate.
    const estRate = computeAllUnitsFallbackRate(validRows as any[]);
    if (estRate !== null) {
      return {
        apr: parseFloat(estRate.toFixed(4)),
        usedCount: validRows.length, excludedCount: 0, totalCount,
        insufficientData: false, noInterestAccrued: false, isZeroInterest: false, isEstimate: true,
        label: "Estimated — original balance data not available",
      };
    }
    return {
      apr: null, usedCount: 0, excludedCount: 0, totalCount,
      insufficientData: totalCount < 3, noInterestAccrued: false, isZeroInterest: false, isEstimate: true,
      label: "Estimated — insufficient per-vehicle data",
    };
  }

  // Step 1: filter to uncurtailed vehicles (|principal - originalAmount| <= $1 or 0.5%,
  // whichever is larger — wider tolerance handles PDF rounding and small admin fees).
  // Note: we check ALL valid rows for curtailment, not just those with interest,
  // so that "no interest accrued" can be detected correctly.
  const uncurtailed = validRows.filter((r) => {
    const orig = n(r.originalAmount);
    const cur = n(r.principal);
    return orig > 0 && Math.abs(cur - orig) <= Math.max(1, orig * 0.005);
  });
  const excludedByCurtailment = validRows.length - uncurtailed.length;

  // Step 2: detect "no interest accrued" — uncurtailed vehicles exist but none have interest
  const uncurtailedWithInterest = uncurtailed.filter((r) => n(r.interest) > 0);
  if (uncurtailed.length >= 3 && uncurtailedWithInterest.length === 0) {
    return {
      apr: 0, usedCount: uncurtailed.length, excludedCount: excludedByCurtailment,
      totalCount, insufficientData: false, noInterestAccrued: false, isZeroInterest: true, isEstimate: false,
      label: "0% interest — cost is fees only",
    };
  }

  // Step 3: further qualify — must have days > 0 for APR math
  const withAprData = uncurtailedWithInterest.filter((r) => n(r.daysOutstanding) > 0);

  if (withAprData.length < 3) {
    return {
      apr: null, usedCount: withAprData.length,
      excludedCount: totalCount - withAprData.length,
      totalCount, insufficientData: true, noInterestAccrued: false, isZeroInterest: false, isEstimate: false,
      label: `Insufficient data — fewer than 3 uncurtailed vehicles (${withAprData.length} found)`,
    };
  }

  // Step 4: per-vehicle APR, median, outlier removal
  const withApr = withAprData.map((r) => ({
    r,
    aprPct: (n(r.interest) / n(r.principal) / n(r.daysOutstanding)) * 365 * 100,
  }));

  const med = median(withApr.map((v) => v.aprPct))!;
  const clean = withApr.filter((v) => Math.abs(v.aprPct - med) <= 2);

  // Step 5: if outlier removal leaves < 3, flag insufficient — do NOT fall back to outlier set
  if (clean.length < 3) {
    return {
      apr: null,
      usedCount: clean.length,
      excludedCount: totalCount - clean.length,
      totalCount,
      insufficientData: true,
      noInterestAccrued: false,
      isZeroInterest: false,
      isEstimate: false,
      label: `Insufficient data after outlier removal — fewer than 3 vehicles (${clean.length} found)`,
    };
  }

  const trueApr = clean.reduce((s, v) => s + v.aprPct, 0) / clean.length;
  const excludedCount = totalCount - clean.length;

  return {
    apr: trueApr,
    usedCount: clean.length,
    excludedCount,
    totalCount,
    insufficientData: false,
    noInterestAccrued: false,
    isZeroInterest: false,
    isEstimate: false,
    label: `True APR — based on uncurtailed vehicles only (${clean.length} used, ${excludedCount} excluded)`,
  };
}
